import { useCallback, useEffect, useRef, useState } from "react";
import { deleteCommande } from "../../../services/api";
import { DELETE_UNDO_SECONDS, tickPendingDelete } from "../model/commandeListRules";

function clearPendingTimers(id, deleteTimers, countdowns) {
    clearTimeout(deleteTimers.get(id));
    clearInterval(countdowns.get(id));
}

// Suppressions différées : chaque commande disparaît de la liste pendant 15 s avant l'appel serveur,
// sauf annulation. Un clic sur une ligne ou le démontage confirme immédiatement les suppressions en attente.
export function useCommandePendingDeletes(onRefresh) {
    const [pendingDeletes, setPendingDeletes] = useState([]);
    const pendingDeletesRef = useRef([]);
    const deleteTimersRef = useRef(new Map());
    const countdownsRef = useRef(new Map());

    useEffect(() => {
        pendingDeletesRef.current = pendingDeletes;
    }, [pendingDeletes]);

    const forgetTimers = useCallback((id) => {
        clearPendingTimers(id, deleteTimersRef.current, countdownsRef.current);
        deleteTimersRef.current.delete(id);
        countdownsRef.current.delete(id);
    }, []);

    const commitPendingDelete = useCallback(async (id, refreshAfter = true) => {
        forgetTimers(id);
        setPendingDeletes(prev => prev.filter(item => item.cmd.id !== id));
        try { await deleteCommande(id); } catch { /* ignore */ }
        if (refreshAfter) onRefresh();
    }, [forgetTimers, onRefresh]);

    const requestDelete = useCallback((cmd) => {
        if (pendingDeletesRef.current.some(item => item.cmd.id === cmd.id)) return;

        setPendingDeletes(prev => [...prev, { cmd, secondsLeft: DELETE_UNDO_SECONDS }]);

        const interval = setInterval(() => {
            setPendingDeletes(prev => tickPendingDelete(prev, cmd.id));
        }, 1000);
        countdownsRef.current.set(cmd.id, interval);

        const timer = setTimeout(() => {
            commitPendingDelete(cmd.id);
        }, DELETE_UNDO_SECONDS * 1000);
        deleteTimersRef.current.set(cmd.id, timer);
    }, [commitPendingDelete]);

    const cancelDelete = useCallback((id) => {
        forgetTimers(id);
        setPendingDeletes(prev => prev.filter(item => item.cmd.id !== id));
    }, [forgetTimers]);

    const flushPendingDeletes = useCallback(() => {
        const pending = pendingDeletesRef.current;
        if (pending.length === 0) return;
        pending.forEach(item => {
            clearPendingTimers(item.cmd.id, deleteTimersRef.current, countdownsRef.current);
            Promise.resolve(deleteCommande(item.cmd.id)).catch(() => {});
        });
        deleteTimersRef.current.clear();
        countdownsRef.current.clear();
        pendingDeletesRef.current = [];
        setPendingDeletes([]);
    }, []);

    useEffect(() => {
        const deleteTimers = deleteTimersRef.current;
        const countdowns = countdownsRef.current;
        return () => {
            pendingDeletesRef.current.forEach(item => {
                clearPendingTimers(item.cmd.id, deleteTimers, countdowns);
                Promise.resolve(deleteCommande(item.cmd.id)).catch(() => {});
            });
            pendingDeletesRef.current = [];
            deleteTimers.clear();
            countdowns.clear();
        };
    }, []);

    return { pendingDeletes, requestDelete, cancelDelete, flushPendingDeletes };
}
